import React, { useRef, useState } from 'react';
import { canDo, getCurrentUser, useCurrentRole } from './RoleSwitcher';
import { importFile, exportFeatures } from '../services/importExportService';
import { logAudit, createAnnotation } from '../services/demoExtrasService';
import { openAnnotationModal } from './AnnotationModal';

/**
 * Floating toolbar over the map: Import / Export / Select / Annotate / Basemap.
 * Drives behavior via a parent-supplied API (mapApi) — the Map component owns the
 * leaflet instance and exposes setBasemap / startSelection / startAnnotation /
 * getCurrentFeatures / getSelectedFeatures.
 */
export default function MapToolbar({ mapApi }) {
  const fileRef = useRef(null);
  const [exportMenu, setExportMenu] = useState(false);
  const [basemapMenu, setBasemapMenu] = useState(false);
  const [busy, setBusy] = useState('');

  const role = useCurrentRole();

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy('import');
    try {
      const fc = await importFile(file);
      if (mapApi?.addImportedLayer) {
        mapApi.addImportedLayer(fc);
      }
      logAudit({ user: getCurrentUser(), role: role.id, action: 'IMPORT', detail: `${file.name} → ${fc.features.length} features`, sources: ['User Upload'], resultCount: fc.features.length });
    } catch (err) {
      alert('Import failed: ' + err.message);
    } finally {
      setBusy('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleExport = async (format) => {
    setExportMenu(false);
    setBusy('export');
    try {
      const features = mapApi?.getSelectedFeatures?.() || mapApi?.getCurrentFeatures?.() || [];
      if (features.length === 0) {
        alert('No features to export. Run a query or make a selection first.');
        return;
      }
      const baseName = mapApi?.getCurrentLayerName?.() || 'export';
      const result = await exportFeatures(features, format, baseName);
      logAudit({ user: getCurrentUser(), role: role.id, action: 'EXPORT', detail: `${result.filename} (${features.length} features)`, sources: ['GIS Server'], resultCount: features.length });
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setBusy('');
    }
  };

  const handleSelect = (mode) => {
    mapApi?.startSelection?.(mode);
    logAudit({ user: getCurrentUser(), role: role.id, action: 'SELECTION_START', detail: `${mode} selection started` });
  };

  const handleAnnotate = async (kind) => {
    if (!mapApi?.startAnnotation) return;
    mapApi.startAnnotation(kind, async (geometry) => {
      const result = await openAnnotationModal({ kind });
      if (!result || !result.title?.trim()) return;
      try {
        const ann = await createAnnotation({
          author: getCurrentUser(),
          kind,
          geometry,
          title: result.title,
          note: result.note,
          color: result.color,
          shared: result.shared ? 1 : 0
        });
        mapApi.addAnnotationLayer?.(ann);
        logAudit({ user: getCurrentUser(), role: role.id, action: 'ANNOTATION', detail: `${kind}: ${result.title}` });
      } catch (e) {
        alert('Save annotation failed: ' + e.message);
      }
    });
  };

  const handleBasemap = (id) => {
    setBasemapMenu(false);
    mapApi?.setBasemap?.(id);
    logAudit({ user: getCurrentUser(), role: role.id, action: 'BASEMAP', detail: id });
  };

  return (
    <div style={wrap}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '4px 10px', borderRadius: 4,
        background: role.color + '22', color: role.color, fontSize: 11, fontWeight: 600,
        marginRight: 4
      }} title={`Logged in as ${role.label}`}>
        <span>{role.icon}</span>
        <span>{role.label}</span>
      </div>
      <div style={divider} />
      {canDo('import') && (
        <>
          <button onClick={() => fileRef.current?.click()} disabled={busy === 'import'} style={btn} title="Import Shapefile (.zip), KMZ, or GeoJSON">
            <span style={icon}>↑</span> {busy === 'import' ? 'Importing…' : 'Import'}
          </button>
          <input ref={fileRef} type="file" accept=".zip,.shp,.kml,.kmz,.geojson,.json" onChange={handleImport} style={{ display: 'none' }} />
        </>
      )}

      {canDo('export') && (
        <div style={{ position: 'relative' }}>
          <button onClick={() => setExportMenu(!exportMenu)} disabled={busy === 'export'} style={btn} title="Export current results or selection">
            <span style={icon}>↓</span> {busy === 'export' ? 'Exporting…' : 'Export'} ▾
          </button>
          {exportMenu && (
            <div style={menu}>
              <button onClick={() => handleExport('shapefile')} style={menuItem}>Shapefile (.zip)</button>
              <button onClick={() => handleExport('kmz')} style={menuItem}>KMZ</button>
              <button onClick={() => handleExport('kml')} style={menuItem}>KML</button>
              <button onClick={() => handleExport('geojson')} style={menuItem}>GeoJSON</button>
            </div>
          )}
        </div>
      )}

      <div style={divider} />

      <button onClick={() => handleSelect('rectangle')} style={btn} title="Rectangle selection">
        <span style={icon}>▭</span> Rect
      </button>
      <button onClick={() => handleSelect('polygon')} style={btn} title="Freeform polygon selection">
        <span style={icon}>⬡</span> Poly
      </button>
      <button onClick={() => mapApi?.clearSelection?.()} style={btn} title="Clear selection">
        Clear
      </button>

      {canDo('annotate') && (
        <>
          <div style={divider} />
          <button onClick={() => handleAnnotate('pin')} style={btn} title="Drop a pin annotation">
            <span style={icon}>📍</span> Pin
          </button>
          <button onClick={() => handleAnnotate('shape')} style={btn} title="Draw an annotation shape">
            <span style={icon}>✎</span> Shape
          </button>
        </>
      )}

      <div style={divider} />

      <div style={{ position: 'relative' }}>
        <button onClick={() => setBasemapMenu(!basemapMenu)} style={btn} title="Switch basemap">
          <span style={icon}>◉</span> Basemap ▾
        </button>
        {basemapMenu && (
          <div style={menu}>
            <button onClick={() => handleBasemap('light')} style={menuItem}>Light (Positron)</button>
            <button onClick={() => handleBasemap('satellite')} style={menuItem}>Satellite (Esri)</button>
            <button onClick={() => handleBasemap('terrain')} style={menuItem}>Terrain (OpenTopo)</button>
            <button onClick={() => handleBasemap('streets')} style={menuItem}>Streets (OSM)</button>
          </div>
        )}
      </div>
    </div>
  );
}

const wrap = {
  position: 'absolute', top: 12, left: 60, zIndex: 1000,
  display: 'flex', alignItems: 'center', gap: 4,
  padding: '4px 6px',
  background: 'rgba(15,23,42,0.85)',
  border: '1px solid rgba(148,163,184,0.25)',
  borderRadius: 6,
  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
  backdropFilter: 'blur(6px)'
};
const btn = {
  background: 'transparent',
  color: '#e2e8f0',
  border: 'none',
  padding: '6px 10px',
  fontSize: 12,
  fontWeight: 500,
  borderRadius: 4,
  cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: 4,
  whiteSpace: 'nowrap'
};
const icon = { fontSize: 11, opacity: 0.85 };
const divider = { width: 1, height: 18, background: 'rgba(148,163,184,0.3)', margin: '0 2px' };
const menu = {
  position: 'absolute', top: '100%', left: 0, marginTop: 4,
  background: 'rgba(15,23,42,0.95)',
  border: '1px solid rgba(148,163,184,0.3)',
  borderRadius: 6,
  boxShadow: '0 8px 16px rgba(0,0,0,0.4)',
  overflow: 'hidden',
  minWidth: 160,
  zIndex: 1001
};
const menuItem = {
  display: 'block', width: '100%', textAlign: 'left',
  background: 'transparent', color: '#e2e8f0', border: 'none',
  padding: '8px 12px', fontSize: 12, cursor: 'pointer',
  whiteSpace: 'nowrap'
};
